<template>
  <div class="page-shell help-page">
    <section class="page-hero help-hero">
      <div class="page-eyebrow">Support Center</div>
      <h1 class="page-title">帮助中心</h1>
      <p class="page-subtitle">
        围绕账号登录、课程学习、案例体验、教师组织和平台使用，提供更清晰的操作指引与常见问题说明。
      </p>

      <div class="help-search modern-card">
        <i class="el-icon-search"></i>
        <input v-model="keyword" placeholder="搜索问题关键词，如：登录、课程、案例、验证码、浏览器" />
      </div>
    </section>

    <section class="page-container help-layout">
      <aside class="help-sidebar modern-card">
        <div class="sidebar-title">帮助目录</div>
        <a
          v-for="item in navItems"
          :key="item.id"
          :href="`#${item.id}`"
          class="sidebar-link"
        >
          <i :class="item.icon"></i>
          <span>{{ item.title }}</span>
        </a>
      </aside>

      <main class="help-main">
        <section id="quick" class="quick-grid">
          <div class="quick-card modern-card" v-for="item in quickEntries" :key="item.title">
            <div class="quick-icon"><i :class="item.icon"></i></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </section>

        <section id="start" class="guide-section modern-card">
          <div class="section-heading">
            <div>
              <h2>快速入门</h2>
              <p>第一次使用平台时，可以按以下流程完成基本操作。</p>
            </div>
          </div>
          <div class="step-list">
            <div class="step-card" v-for="(step, index) in startSteps" :key="step.title">
              <div class="step-no">{{ index + 1 }}</div>
              <div>
                <h3>{{ step.title }}</h3>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="roles" class="guide-section modern-card">
          <div class="section-heading">
            <div>
              <h2>角色使用指南</h2>
              <p>教师、学员和管理员可按自身角色查看重点功能。</p>
            </div>
          </div>
          <div class="role-grid">
            <div class="role-card" v-for="role in rolesGuide" :key="role.title">
              <div class="role-top">
                <i :class="role.icon"></i>
                <h3>{{ role.title }}</h3>
              </div>
              <ul>
                <li v-for="item in role.items" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="faq" class="faq-section modern-card">
          <div class="section-heading">
            <div>
              <h2>常见问题</h2>
              <p>根据当前关键词自动筛选常见说明。</p>
            </div>
            <span class="faq-count">共 {{ filteredFaqs.length }} 条</span>
          </div>
          <el-collapse v-model="activeFaq" accordion>
            <el-collapse-item
              v-for="(faq, index) in filteredFaqs"
              :key="faq.q"
              :title="faq.q"
              :name="String(index)"
            >
              <p>{{ faq.a }}</p>
            </el-collapse-item>
          </el-collapse>
          <div v-if="filteredFaqs.length === 0" class="empty-result">
            暂未找到相关问题，请换一个关键词，或联系平台管理员。
          </div>
        </section>

        <section id="support" class="support-card modern-card">
          <div>
            <h2>技术支持与服务</h2>
            <p>如遇账号、权限、案例资源或平台访问问题，请优先联系本单位平台管理员或项目对接人员。</p>
          </div>
          <div class="support-info">
            <span><strong>服务单位</strong>北京数字维尔科技有限公司</span>
            <span><strong>适用场景</strong>应急教学、在线培训、案例学习、情景训练</span>
            <span><strong>推荐环境</strong>最新版 Chrome / Edge 浏览器</span>
          </div>
        </section>
      </main>
    </section>
  </div>
</template>

<script>
export default {
  name: "Help",
  data() {
    return {
      keyword: "",
      activeFaq: "0",
      navItems: [
        { id: "quick", title: "常用入口", icon: "el-icon-menu" },
        { id: "start", title: "快速入门", icon: "el-icon-guide" },
        { id: "roles", title: "角色指南", icon: "el-icon-user" },
        { id: "faq", title: "常见问题", icon: "el-icon-question" },
        { id: "support", title: "技术支持", icon: "el-icon-service" },
      ],
      quickEntries: [
        { title: "账号登录", desc: "使用分配账号、密码和验证码进入平台。", icon: "el-icon-key" },
        { title: "课程学习", desc: "登录后进入课程中心，查看课程内容和学习任务。", icon: "el-icon-reading" },
        { title: "经典案例", desc: "通过案例资源库进入情景化案例学习和训练。", icon: "el-icon-collection" },
        { title: "学习管理", desc: "教师和管理员可查看课程、学员与学习过程信息。", icon: "el-icon-data-analysis" },
      ],
      startSteps: [
        { title: "登录平台", desc: "在登录页输入账号、密码和验证码，进入对应角色页面。" },
        { title: "选择课程或案例", desc: "学员可进入课程学习或经典案例，教师可进入教学管理入口。" },
        { title: "开展情景学习", desc: "根据课程内容、案例说明和情景资源完成学习、体验或训练任务。" },
        { title: "查看记录与反馈", desc: "根据账号权限查看学习过程、课程记录和相关反馈信息。" },
      ],
      rolesGuide: [
        {
          title: "教师使用指南",
          icon: "el-icon-s-custom",
          items: ["管理课程与教学资源", "组织情景化教学", "查看学员学习记录", "开展教学复盘与反馈"],
        },
        {
          title: "学员使用指南",
          icon: "el-icon-user-solid",
          items: ["登录并进入我的课程", "查看课程内容", "进入经典案例学习", "完成学习任务与反馈"],
        },
        {
          title: "管理员支持",
          icon: "el-icon-setting",
          items: ["账号与权限管理", "课程与案例资源维护", "平台运行支持", "问题收集与服务对接"],
        },
      ],
      faqs: [
        { q: "如何登录平台？", a: "点击右上角登录入口，输入账号、密码和验证码后即可登录。登录成功后，系统会根据账号角色进入对应页面。" },
        { q: "忘记密码怎么办？", a: "请联系本单位平台管理员或项目对接人员进行密码重置。" },
        { q: "验证码无法显示怎么办？", a: "请点击验证码图片刷新，或检查浏览器是否拦截图片资源。建议使用 Chrome 或 Edge 浏览器访问。" },
        { q: "如何查看经典案例？", a: "进入顶部导航的“经典案例”，选择案例卡片即可进入对应课程、案例或情景学习页面。" },
        { q: "如何进入我的课程？", a: "登录后点击“我的课程”，系统会根据当前账号角色进入教师管理或学生中心。" },
        { q: "案例加载较慢怎么办？", a: "部分情景案例包含图片、视频或三维资源，首次加载可能较慢。建议保持稳定网络环境并耐心等待。" },
        { q: "教师和学员看到的页面为什么不一样？", a: "平台会根据账号角色分配不同权限。教师侧更偏课程和学员管理，学员侧更偏课程学习和案例体验。" },
        { q: "推荐使用什么浏览器？", a: "推荐使用最新版 Chrome、Edge 或其他现代浏览器访问，不建议使用过旧浏览器。" },
      ],
    };
  },
  computed: {
    filteredFaqs() {
      const kw = this.keyword.trim().toLowerCase();
      if (!kw) return this.faqs;
      return this.faqs.filter((item) =>
        `${item.q}${item.a}`.toLowerCase().includes(kw)
      );
    },
  },
};
</script>

<style scoped lang="scss">
.help-hero {
  padding-bottom: 34px;
}

.help-search {
  margin-top: 30px;
  max-width: 760px;
  height: 62px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 22px;
}

.help-search i {
  color: var(--secondary-color);
  font-size: 22px;
}

.help-search input {
  border: none;
  outline: none;
  flex: 1;
  height: 100%;
  background: transparent;
  font-size: 16px;
  color: var(--dark-text);
}

.help-layout {
  display: grid;
  grid-template-columns: 230px 1fr;
  gap: 24px;
  align-items: start;
}

.help-sidebar {
  position: sticky;
  top: 96px;
  padding: 18px;
}

.sidebar-title {
  color: var(--brand-blue-deep);
  font-weight: 900;
  margin: 4px 0 14px;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px;
  border-radius: 14px;
  color: #526274;
  font-weight: 800;
  transition: all 0.2s ease;
}

.sidebar-link:hover {
  color: var(--secondary-color);
  background: rgba(47, 111, 237, 0.08);
}

.quick-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 24px;
}

.quick-card {
  padding: 24px 20px;
  transition: all 0.25s ease;
}

.quick-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 50px rgba(15, 47, 95, 0.12);
}

.quick-icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(47, 111, 237, 0.10);
  color: var(--secondary-color);
  font-size: 24px;
  margin-bottom: 18px;
}

.quick-card h3,
.role-card h3,
.step-card h3 {
  color: var(--brand-blue-deep);
  font-size: 19px;
  font-weight: 900;
  margin-bottom: 10px;
}

.quick-card p,
.step-card p {
  color: var(--gray-text);
  line-height: 1.7;
}

.guide-section,
.faq-section,
.support-card {
  padding: 30px;
  margin-top: 24px;
}

.step-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.step-card {
  display: flex;
  gap: 15px;
  padding: 18px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.step-no {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: grid;
  place-items: center;
  border-radius: 13px;
  background: linear-gradient(135deg, var(--secondary-color), #69a3ff);
  color: #fff;
  font-weight: 900;
}

.role-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.role-card {
  padding: 22px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.role-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.role-top i {
  color: var(--secondary-color);
  font-size: 22px;
}

.role-card li {
  position: relative;
  padding-left: 18px;
  color: #475569;
  line-height: 2;
}

.role-card li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color);
}

.faq-count {
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(244, 104, 36, 0.10);
  color: var(--primary-color);
  font-weight: 900;
  font-size: 13px;
}

::v-deep .el-collapse {
  border: none;
}

::v-deep .el-collapse-item__header {
  min-height: 56px;
  border-bottom-color: #e5edf7;
  color: var(--brand-blue-deep);
  font-size: 16px;
  font-weight: 900;
}

::v-deep .el-collapse-item__content {
  color: #64748b;
  line-height: 1.8;
  padding: 0 0 20px;
}

.empty-result {
  padding: 28px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 18px;
}

.support-card {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 24px;
  align-items: center;
  margin-bottom: 30px;
}

.support-card h2 {
  color: var(--brand-blue-deep);
  font-size: 26px;
  font-weight: 900;
  margin-bottom: 10px;
}

.support-card p {
  color: var(--gray-text);
  line-height: 1.8;
}

.support-info {
  display: grid;
  gap: 10px;
}

.support-info span {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fafc;
  color: #475569;
}

.support-info strong {
  min-width: 72px;
  color: var(--brand-blue-deep);
}

@media (max-width: 980px) {
  .help-layout {
    grid-template-columns: 1fr;
  }

  .help-sidebar {
    position: static;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }

  .quick-grid,
  .role-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .support-card {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .help-sidebar,
  .quick-grid,
  .role-grid,
  .step-list {
    grid-template-columns: 1fr;
  }

  .guide-section,
  .faq-section,
  .support-card {
    padding: 22px;
  }
}


/* V1.5 visual refinement: make support center lighter and more delicate. */
.help-search {
  height: 54px;
  border-radius: 20px;
  box-shadow: 0 12px 32px rgba(15, 47, 95, 0.08);
}

.help-search input {
  font-size: 15px;
}

.help-layout {
  grid-template-columns: 210px 1fr;
  gap: 20px;
}

.help-sidebar {
  padding: 18px;
  border-radius: 22px;
}

.sidebar-title {
  font-size: 15px;
}

.sidebar-link {
  min-height: 38px;
  font-size: 14px;
}

.quick-card,
.guide-section,
.faq-section,
.support-card {
  border-radius: 22px;
}

.quick-card {
  padding: 20px;
}

.quick-icon,
.step-no {
  width: 38px;
  height: 38px;
  border-radius: 14px;
}

.quick-card h3,
.step-card h3,
.role-card h3 {
  font-size: 16px;
}

.quick-card p,
.step-card p,
.role-card li,
.faq-section p,
.support-card p,
.support-info span {
  font-size: 14px;
}

@media (max-width: 860px) {
  .help-layout {
    grid-template-columns: 1fr;
  }
}

</style>
